import { createApi } from '@reduxjs/toolkit/query/react';

import { ReauthBaseQuery } from '@skynet/data-auth-api';
import { dexApiBaseQuery } from '@skynet/data-dex-api';

import { DEX_TRANSACTIONS_API_ENDPOINTS } from './config';
import { createUrl } from './createUrl';
import type {
  DexServicesResponse,
  DexTransactionResponse,
  DexTransactionsResponseError,
  GetListTransactionsParams,
} from './types';

/**
 * DEX transactions API on the shared DEX base query, with automatic token refresh.
 *
 * `withdraw=1` is appended as a query parameter when the page asks for
 * withdrawals instead of tasks.
 */
export const dexTransactionsApi = createApi({
  reducerPath: 'dexTransactionsApi',
  tagTypes: ['DexTransactions', 'DexServices'],
  baseQuery: ReauthBaseQuery(dexApiBaseQuery),
  endpoints: (build) => ({
    getListTransactions: build.query<
      DexTransactionResponse | DexTransactionsResponseError,
      GetListTransactionsParams
    >({
      query: (params = {}) => {
        const {
          pid = 0,
          tid = 0,
          success = 1,
          type = '-1',
          page = 0,
          limit = 20,
          withdraw,
        } = params;
        const url = createUrl(DEX_TRANSACTIONS_API_ENDPOINTS.getListTransactions.path, {
          pid,
          tid,
          success,
          type,
          limit,
          page: page === 0 ? 0 : page - 1,
        });
        return {
          url: withdraw ? `${url}?withdraw=1` : url,
          method: DEX_TRANSACTIONS_API_ENDPOINTS.getListTransactions.method,
        };
      },
      providesTags: ['DexTransactions'],
    }),
    getServices: build.query<DexServicesResponse, void>({
      query: () => ({
        url: createUrl(DEX_TRANSACTIONS_API_ENDPOINTS.getServices.path, { bId: 1 }),
        method: DEX_TRANSACTIONS_API_ENDPOINTS.getServices.method,
      }),
      providesTags: ['DexServices'],
    }),
  }),
});

export const { useGetListTransactionsQuery, useGetServicesQuery, useLazyGetListTransactionsQuery } =
  dexTransactionsApi;
